
import { computed, defineComponent, nextTick, onBeforeUnmount, PropType, ref, watch } from 'vue'

export type IntegrationImagePreviewSlide = {
    src: string
    title?: string
    kind?: 'image' | 'video'
}

export default defineComponent({
    name: 'IntegrationImagePreview',
    props: {
        value: {
            type: Boolean,
            required: true,
        },
        slides: {
            type: Array as PropType<IntegrationImagePreviewSlide[]>,
            default: () => [],
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
    },
    setup(props, { emit }) {
        const loadError = ref(false)
        const imageLoading = ref(false)
        const previewImageEl = ref<HTMLImageElement | null>(null)
        const activeIndex = ref(0)

        const dialogOpen = computed({
            get: () => props.value,
            set: (opened: boolean) => {
                emit('input', opened)
            },
        })

        const clampIndex = (index: number) => {
            if (!props.slides.length) return 0
            return ((index % props.slides.length) + props.slides.length) % props.slides.length
        }

        const startImageLoading = () => {
            loadError.value = false
            imageLoading.value = Boolean(currentSlide.value.src)
        }

        const syncActiveIndex = () => {
            activeIndex.value = clampIndex(props.initialIndex)
            startImageLoading()
        }

        const checkCachedImageLoaded = () => {
            const el = previewImageEl.value
            if (el?.complete && el.naturalWidth > 0) {
                onImageLoad()
            }
        }

        const currentSlide = computed(() => {
            const slide = props.slides[activeIndex.value]
            return {
                src: slide?.src || '',
                title: slide?.title || (slide?.kind === 'video' ? 'Просмотр видео' : 'Просмотр изображения'),
                kind: slide?.kind || 'image',
            }
        })
        const isVideoSlide = computed(() => currentSlide.value.kind === 'video')

        const canNavigate = computed(() => props.slides.length > 1)

        const displayTitle = computed(() => {
            const title = currentSlide.value.title
            if (!canNavigate.value) return title
            return `${title} из ${props.slides.length}`
        })

        watch(
            () => currentSlide.value.src,
            () => {
                startImageLoading()
                nextTick(() => checkCachedImageLoaded())
            }
        )

        watch(
            () => props.value,
            opened => {
                if (opened) {
                    syncActiveIndex()
                    nextTick(() => checkCachedImageLoaded())
                    window.addEventListener('keydown', onKeydown)
                } else {
                    loadError.value = false
                    imageLoading.value = false
                    window.removeEventListener('keydown', onKeydown)
                }
            }
        )

        watch(
            () => props.initialIndex,
            () => {
                if (props.value) syncActiveIndex()
            }
        )

        onBeforeUnmount(() => {
            window.removeEventListener('keydown', onKeydown)
        })

        const close = () => {
            emit('input', false)
        }

        const onDialogInput = (opened: boolean) => {
            dialogOpen.value = opened
        }

        const onImageLoad = () => {
            imageLoading.value = false
        }

        const onImageError = () => {
            imageLoading.value = false
            loadError.value = true
        }

        const goTo = (index: number) => {
            activeIndex.value = clampIndex(index)
            startImageLoading()
            nextTick(() => checkCachedImageLoaded())
        }

        const goPrev = () => {
            goTo(activeIndex.value - 1)
        }

        const goNext = () => {
            goTo(activeIndex.value + 1)
        }

        const onKeydown = (event: KeyboardEvent) => {
            if (!props.value) return
            if (event.key === 'Escape') {
                close()
                return
            }
            if (!canNavigate.value) return
            if (event.key === 'ArrowLeft') {
                event.preventDefault()
                goPrev()
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault()
                goNext()
            }
        }

        return {
            dialogOpen,
            loadError,
            imageLoading,
            previewImageEl,
            activeIndex,
            currentSlide,
            isVideoSlide,
            canNavigate,
            displayTitle,
            close,
            onDialogInput,
            onImageLoad,
            onImageError,
            goTo,
            goPrev,
            goNext,
        }
    },
})
